/*
 * Project: Goalscape
 * File:    dateUtils.js
 * Path:    src/utils/dateUtils.js
 * Description: Utility functions for date formatting and manipulation.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Consider using a dedicated library like Moment.js or date-fns for more advanced date formatting and timezone handling.
 */
import {
    format,
    isToday,
    isTomorrow,
    isPast,
    isFuture,
    differenceInCalendarDays,
    differenceInHours,
    parseISO,
    isSameYear,
    startOfWeek,
    endOfWeek,
    addWeeks,
    isWithinInterval,
} from 'date-fns';

/*
 * Function: formatDate
 * Description: Formats a date string into a human-readable relative time.
 * Parameters:
 *   dateString: string - ISO date string to format.
 * Returns: string - Formatted relative time (e.g., "Just now", "5m ago", "2 weeks ago").
 * Usage:
 *   const timeAgo = formatDate('2025-03-15T10:30:00Z');
 *   // Returns something like "2h ago" or "Yesterday"
 * AI Hint: Implement localization to support different date formats and languages.
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString); // Assuming dateString is a valid date representation
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 1) {
        return 'Just now';
    } else if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
        return `${diffHours}h ago`;
    } else if (diffDays === 0 && isToday(date)) { // Check if it's actually today
        return 'Today';
    } else if (diffDays === 1 && isPast(date) && !isToday(date)) { // Check if it's actually yesterday
        return 'Yesterday';
    } else if (diffDays < 7) {
        return `${diffDays} days ago`;
    } else if (diffDays < 30) {
        return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    }
};

/**
 * Formats a due date for display.
 * @param {string | Date} dateStringOrObject - The due date (ISO string or Date object).
 * @param {boolean} isCompleted - Whether the task is completed.
 * @returns {string} Formatted due date string.
 */
export const formatDueDate = (dateStringOrObject, isCompleted) => {
    if (!dateStringOrObject) return 'No due date';

    const date = typeof dateStringOrObject === 'string' ? parseISO(dateStringOrObject) : dateStringOrObject;
    const now = new Date();

    if (isCompleted) {
        // Example: "Completed on Mar 20" or "Completed on Mar 20, 2023" if different year
        const formatString = isSameYear(date, now) ? 'MMM d' : 'MMM d, yyyy';
        return `Completed on ${format(date, formatString)}`;
    }

    let formatString;
    if (isToday(date)) {
        formatString = "'Today at' h:mm a";
    } else if (isTomorrow(date)) {
        formatString = "'Tomorrow at' h:mm a";
    } else {
        formatString = isSameYear(date, now) ? 'MMM d \'at\' h:mm a' : 'MMM d, yyyy \'at\' h:mm a';
    }
    return format(date, formatString);
};

export const formatStartDate = (dateStringOrObject) => {
    if (!dateStringOrObject) return 'No start date';

    const date = typeof dateStringOrObject === 'string' ? parseISO(dateStringOrObject) : dateStringOrObject;
    const now = new Date();

    if (isPast(date)) {
        const formatString = isSameYear(date, now) ? 'MMM d' : 'MMM d, yyyy';
        return `Started on ${format(date, formatString)}`;
    }

    let formatString;
    if (isToday(date)) {
        formatString = "'Today at' h:mm a";
    } else if (isTomorrow(date)) {
        formatString = "'Tomorrow at' h:mm a";
    } else {
        formatString = isSameYear(date, now) ? 'MMM d \'at\' h:mm a' : 'MMM d, yyyy \'at\' h:mm a';
    }
    return `Starts ${format(date, formatString)}`;
};

/**
 * Gets the status of a due date (e.g., overdue, due soon).
 * @param {string | Date} dateStringOrObject - The due date (ISO string or Date object).
 * @param {boolean} isCompleted - Whether the task is completed.
 * @returns {{status: string, text: string, color: string}} Object indicating status.
 */
export const getDueDateStatus = (dateStringOrObject, isCompleted) => {
    if (isCompleted) {
        return { status: 'completed', text: 'Completed', color: '#36B37E' }; // Green
    }

    if (!dateStringOrObject) {
        return { status: 'no_date', text: 'No Due Date', color: '#6B778C' }; // Neutral / Gray
    }

    const date = typeof dateStringOrObject === 'string' ? parseISO(dateStringOrObject) : dateStringOrObject;

    if (isPast(date) && !isToday(date)) { // Ensure it's not just past time today
        return { status: 'overdue', text: 'Overdue', color: '#FF5630' }; // Red
    }

    const hoursDiff = differenceInHours(date, new Date());

    if (hoursDiff >= 0 && hoursDiff <= 48) { // Due within the next 48 hours
        if (isToday(date)) {
             return { status: 'due_today', text: 'Due Today', color: '#FFAB00' }; // Orange/Amber
        }
        return { status: 'due_soon', text: 'Due Soon', color: '#FFAB00' }; // Orange/Amber
    }

    if (isFuture(date)) {
        return { status: 'upcoming', text: 'Upcoming', color: '#0065FF' }; // Blue
    }

    // Default for past times today that aren't "overdue" yet by full day logic
    // or any other edge cases.
    return { status: 'due_today_past', text: 'Due Today', color: '#FFAB00' };
};

export const isDateThisWeek = (dateString) => {
    if (!dateString) return false;
    const date = parseISO(dateString);
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 }); // Assuming week starts on Monday
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return isWithinInterval(date, { start, end });
};

export const isDateNextWeek = (dateString) => {
    if (!dateString) return false;
    const date = parseISO(dateString);
    const now = new Date();
    const startOfNextWeek = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
    const endOfNextWeek = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
    return isWithinInterval(date, { start: startOfNextWeek, end: endOfNextWeek });
};